/* ==========================================================================
  LD Cart Meta Module
  - Unit label near qty (Pakete/Stück)
  - Meta under qty:
      Uses LINE TOTALS (final_line_price/original_line_price) to show correct totals for chosen quantity.
  - m² / lfm are ALWAYS CEIL to integer (sync with calculator)
  - Prefers _fc_* properties if present (single source of truth)
  - Disables qty for calculator-managed add-ons (Sockel/Tritt/Kleber) without changing +/- layout
  - Muster: hide meta box + hide qty selector entirely
  - Calculator add-ons (_fc_group='addon'): stays independent (NO bundle sync)
  - "Sie sparen" (drawer bottom): sum from line totals + UVP(compare_at)
  - Subtotal: normalize currency sign to RIGHT ("12,34 €") even if theme money_format is "€{{amount}}"
  - Stable across Impulse DOM replacement (data-products replaced on every qty change)
  - Subtotal cents source of truth: .cart__items[data-cart-subtotal] from cart.ajax.liquid
  - MutationObserver is scoped + ignores own meta mutations to prevent feedback loops

  FIX (2026-02-26, debug + orphan cleanup hardening):
  - Fix orphanDeps filter logic (remove deps when main is gone)
  - More robust main detection (role/kind/unit + fallback by props/title)
  - Add optional DEBUG logs
============================================================================ */
(() => {
  'use strict';

  // ✅ single-init guard
  if (window.__LD_CART_META_INITED__) return;
  window.__LD_CART_META_INITED__ = true;

  // 🔎 set true to see logs
  const DEBUG = false;
  const dlog = (...a) => { if (DEBUG) console.log('[ld-cart-meta]', ...a); };

  const SELECTORS = {
    item: '.cart__item',
    meta: '[data-ld-cart-meta]',
    unit: '[data-ld-unit-label]',
    qtyRow: '.ld-cart-qty-row',
    qtyWrap: '.js-qty__wrapper',
    qtyInput: 'input.js-qty__num',
    minus: 'button.js-qty__adjust--minus',
    plus: 'button.js-qty__adjust--plus',

    priceCol: '.cart__item-price-col',
    priceStrike: '.cart__price--strikethrough',

    // Drawer "Sie sparen"
    savingsValue: '.drawer [data-ld-cart-savings-value]',

    // Any subtotal elements you added (drawer + cart page)
    subtotals: '[data-subtotal]',

    // AJAX cart response hook (cart.ajax.liquid)
    ajaxSubtotalHook: '.cart__items[data-cart-subtotal]'
  };

  // -----------------------
  // Utils
  // -----------------------
  const toNum = (v, fb = 0) => {
    const n = Number(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : fb;
  };

  const safeJson = (s) => {
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) {
        const obj = {};
        parsed.forEach(pair => {
          if (Array.isArray(pair) && pair.length >= 2) obj[String(pair[0])] = pair[1];
        });
        return obj;
      }
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch {
      return {};
    }
  };

  // Always "12,34 €"
  const money = (cents) => {
    const v = (Number(cents) || 0) / 100;
    const n = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(v);
    return `${n} €`;
  };

  const ceilInt = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.ceil(x));
  };

  const round2 = (n) => (Math.round((Number(n) || 0) * 100) / 100);

  const getProp = (props, keys) => {
    if (!props || typeof props !== 'object') return null;
    const map = {};
    Object.keys(props).forEach(k => (map[String(k).toLowerCase().trim()] = props[k]));
    for (const k of keys) {
      const v = map[String(k).toLowerCase().trim()];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
    }
    return null;
  };

  const isSampleLine = (props) => {
    const h = getProp(props, ['_sample_for_handle']);
    const t = getProp(props, ['_sample_for_title']);
    const u = getProp(props, ['_sample_for_url']);
    const i = getProp(props, ['_sample_for_image']);
    return !!(h || t || u || i);
  };

  const isSockelAccessory = (title = '') => {
    const t = String(title || '').toLowerCase();
    return (
      t.includes('montageclip') ||
      t.includes('montage clips') ||
      t.includes('clip') ||
      t.includes('endkappe') ||
      t.includes('endkappen') ||
      t.includes('innenecke') ||
      t.includes('innenecken') ||
      t.includes('aussenecke') ||
      t.includes('aussenecken') ||
      t.includes('ecke') ||
      t.includes('ecken')
    );
  };

  const isSockelProduct = ({ title = '', type = '', tags = '' } = {}) => {
    const t = String(title).toLowerCase();
    const ty = String(type).toLowerCase();
    const tg = String(tags).toLowerCase();
    return (
      t.includes('sockel') ||
      t.includes('sockelleiste') ||
      t.includes('sockelleisten') ||
      ty.includes('sockel') ||
      tg.includes('sockel')
    );
  };

  const detectKind = ({
    title,
    type,
    tags,
    packageSize,
    lfmPerPiece,
    hasM2Prop,
    hasLfmProp,
    fcUnit,
    overrideKind = ''
  }) => {
    const t = (title || '').toLowerCase();
    const ty = (type || '').toLowerCase();
    const tg = (tags || '').toLowerCase();
    const u = (fcUnit || '').toLowerCase();

    if (u === 'lfm') return 'sockel';

    if (u === 'm2') {
      const trittHints = ['tritt', 'trittschall', 'unterlage', 'dämm', 'daemm', 'akustik'];
      const hasTrittWord = trittHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));

      const floorHints = ['vinylboden', 'laminat', 'parkett', 'designboden', 'boden', 'landhausdiele', 'klick'];
      const looksLikeFloor = floorHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));

      const accessoryHints = ['trittschalldämmung', 'trittschalldaemmung', 'dämmung', 'daemmung', 'unterlage', 'akustik'];
      const looksLikeAccessory =
        accessoryHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k)) ||
        tg.includes('zubehör') || ty.includes('zubehör');

      const isTritt = hasTrittWord && !looksLikeFloor && (looksLikeAccessory || !(packageSize > 0));
      return isTritt ? 'tritt' : 'floor';
    }

    // Cart-line unit override (from cart-item.liquid data-cart-kind-forced).
    // Placed AFTER fcUnit early-returns so calculator-added lines are never
    // affected. Whitelisted so an empty/invalid value falls through to the
    // heuristics below — zero regression for products without an override.
    const ov = String(overrideKind || '').toLowerCase();
    if (ov === 'floor' || ov === 'sockel' || ov === 'tritt' || ov === 'piece') return ov;

    if (isSockelProduct({ title, type, tags }) && !isSockelAccessory(title)) return 'sockel';
    if (isSockelAccessory(title) && !(hasLfmProp || lfmPerPiece > 0)) return 'piece';

    const trittHints = ['tritt', 'trittschall', 'unterlage', 'dämm', 'daemm', 'akustik'];
    const isTritt = trittHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));

    const isSockel = (hasLfmProp || lfmPerPiece > 0);
    const isFloor = hasM2Prop || (packageSize > 0 && !isSockel && !isTritt);

    if (isSockel) return 'sockel';
    if (isTritt) return 'tritt';
    if (isFloor) return 'floor';
    return 'piece';
  };

  const shouldDisableQty = () => true; // FC: lock qty for ALL cart items — Aug 2026
  /* Original selective logic — restore this body to revert:
  const shouldDisableQty = ({ kind, title = '', type = '', tags = '', props = {} }) => {
    const t = String(title).toLowerCase();
    const ty = String(type).toLowerCase();
    const tg = String(tags).toLowerCase();

    const fc = String(getProp(props, ['_fc']) || '').trim();
    const fromCalc = fc === '1';

    const isKleber = (() => {
      const s = `${t} ${ty} ${tg}`;
      const glueRx = /\b(kleber|klebstoff|designbelagsklebstoff|dispersionskleber|kontaktkleber|parkettkleber)\b/i;
      const excludeRx = /\b(klebevinyl|klebevinylboden|klebe\-vinyl)\b/i;
      return glueRx.test(s) && !excludeRx.test(s);
    })();

    if (kind === 'floor') return false;

    const isCalcAddon = fromCalc && (kind === 'sockel' || kind === 'tritt' || isKleber);

    const isPeStandard =
      fromCalc &&
      t.includes('pe standard') &&
      t.includes('2.0') &&
      (t.includes('tritt') || t.includes('trittschall'));

    const offerKeywords = ['angebot', 'aktion', 'set', 'bundle'];
    const isOfferLike = offerKeywords.some(k => tg.includes(k) || ty.includes(k) || t.includes(k));
    const isAddonKind = (kind === 'sockel' || kind === 'tritt' || isKleber);

    return isCalcAddon || isPeStandard || (isOfferLike && isAddonKind);
  };*/

  const setQtyDisabled = (itemRoot, disabled) => {
    const qtyWrap = itemRoot.querySelector(SELECTORS.qtyWrap);
    if (!qtyWrap) return;

    qtyWrap.classList.toggle('ld-qty-disabled', !!disabled);

    const input = qtyWrap.querySelector(SELECTORS.qtyInput);
    const minus = qtyWrap.querySelector(SELECTORS.minus);
    const plus = qtyWrap.querySelector(SELECTORS.plus);

    if (input) input.disabled = !!disabled;
    if (minus) minus.disabled = !!disabled;
    if (plus) plus.disabled = !!disabled;
  };

  const setQtyHidden = (itemRoot, hidden) => {
    itemRoot.classList.toggle('ld-is-sample', !!hidden);
    const row = itemRoot.querySelector(SELECTORS.qtyRow);
    if (row) row.style.display = hidden ? 'none' : '';
  };

  // uvpMode:
  //  - 'label'      => "UVP: <s>..</s>"
  //  - 'strikeOnly' => "<s>..</s>" (NO UVP label)
  const lineTpl = (label, value, uvp, uvpMode = 'label') => {
    const hasUvp = !!uvp && String(uvp).trim() !== '';
    const uvpHtml = !hasUvp
      ? ''
      : (uvpMode === 'strikeOnly'
        ? `<div class="ld-meta-uvp"><s>${uvp}</s></div>`
        : `<div class="ld-meta-uvp">UVP: <s>${uvp}</s></div>`
      );

    return `
      <div class="ld-meta-line">
        <div class="ld-meta-left">${label}</div>
        ${uvpHtml}
        <div class="ld-meta-right">${value}</div>
      </div>
    `;
  };

  const syncRightPriceColumn = (itemRoot, originalLine, finalLine, hasDiscount) => {
    const col = itemRoot.querySelector(SELECTORS.priceCol);
    if (!col) return;

    const strike = col.querySelector(SELECTORS.priceStrike);
    const discount = col.querySelector('.cart__price.cart__discount');

    if (hasDiscount) {
      if (strike) strike.textContent = money(originalLine);
      if (discount) discount.textContent = money(finalLine);
    } else {
      const main = col.querySelector('.cart__price:not(.cart__discount)');
      if (main) main.textContent = money(finalLine);
    }
  };

  // -----------------------
  // Bundle Sync (Drawer)
  // -----------------------
  const BUNDLE = {
    enabled: true,
    debounceMs: 260,
    glueKgPerM2: 0.3,
    maxQty: 999999,

    // runtime state
    isSyncing: false,
    syncingParents: new Set(),
    lastMainQtyByParent: new Map(),
    scheduled: new Map(), // parent -> timerId

    // orphan cleanup (when main is deleted, deps must be removed)
    cleanupDebounceMs: 260,
    cleanupScheduled: new Map(), // parent -> timerId

    // remove-click hook
    pendingRemovedParents: new Map() // parent -> ts
  };

  const isDrawerOpen = () => {
    const d = document.querySelector('#CartDrawer');
    return !!(d && (d.classList.contains('drawer--is-open') || d.getAttribute('aria-hidden') === 'false'));
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Failed /cart.js (${res.status})`);
    return res.json();
  };

  const cartChange = async (lineKey, quantity, properties) => {
    const id = String(lineKey || '').trim();
    if (!id) return null;

    const payload = {
      id,
      quantity: Math.max(0, Math.min(BUNDLE.maxQty, Number(quantity) || 0))
    };

    if (properties && typeof properties === 'object') payload.properties = properties;

    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      console.warn('cartChange failed', await res.text().catch(() => ''));
      return null;
    }
    return res.json();
  };

  // ✅ single request bulk update (minimal calls)
  const cartBulkUpdate = async (updatesObj) => {
    const updates = updatesObj && typeof updatesObj === 'object' ? updatesObj : {};
    const keys = Object.keys(updates);
    if (!keys.length) return null;

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!res.ok) {
      console.warn('cartBulkUpdate failed', await res.text().catch(() => ''));
      return null;
    }
    return res.json();
  };

  const getBundleParent = (props) => {
    const p = getProp(props, ['_fc_parent']);
    return p ? String(p).trim() : '';
  };

  const isFromCalculator = (props) => String(getProp(props, ['_fc']) || '').trim() === '1';

  const isModalAddon = (props) => {
    const g = String(getProp(props, ['_fc_group']) || '').toLowerCase().trim();
    return g === 'addon';
  };

  // --- robust role/kind getters (NO risky fallback-to-dep here)
  const getFcRole = (props) => String(getProp(props, ['_fc_role']) || '').toLowerCase().trim();
  const getFcKind = (props) => String(getProp(props, ['_fc_kind']) || '').toLowerCase().trim();
  const getFcUnit = (props) => String(getProp(props, ['_fc_unit']) || '').toLowerCase().trim();
  const getFcGroup = (props) => String(getProp(props, ['_fc_group']) || '').toLowerCase().trim();

  // fallback kind inference from cart item title/type + properties (when _fc_kind is missing)
  const inferKindFromCart = ({ title = '', productType = '', tags = '', props = {} }) => {
    const t = String(title || '').toLowerCase();
    const ty = String(productType || '').toLowerCase();
    const tg = String(tags || '').toLowerCase();

    const unit = getFcUnit(props);
    if (unit === 'lfm') return 'sockel';
    if (unit === 'm2') {
      // differentiate floor vs tritt by keywords
      const trittHints = ['tritt', 'trittschall', 'unterlage', 'dämm', 'daemm', 'akustik'];
      const floorHints = ['vinylboden', 'laminat', 'parkett', 'designboden', 'boden', 'landhausdiele', 'klick'];
      const hasTritt = trittHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));
      const looksFloor = floorHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));
      return (hasTritt && !looksFloor) ? 'tritt' : 'floor';
    }

    // if there is explicit m2/lfm properties, use them
    const hasM2 = getProp(props, ['m2', 'm²', '_fc_total']) !== null && (getFcUnit(props) === 'm2' || getProp(props, ['m2', 'm²']) !== null);
    const hasLfm = getProp(props, ['lfm']) !== null || getFcUnit(props) === 'lfm';

    if (hasLfm) return 'sockel';
    if (hasM2) {
      const trittHints = ['tritt', 'trittschall', 'unterlage', 'dämm', 'daemm', 'akustik'];
      const floorHints = ['vinylboden', 'laminat', 'parkett', 'designboden', 'boden', 'landhausdiele', 'klick'];
      const hasTritt = trittHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));
      const looksFloor = floorHints.some(k => t.includes(k) || ty.includes(k) || tg.includes(k));
      return (hasTritt && !looksFloor) ? 'tritt' : 'floor';
    }

    // glue hint
    const glueRx = /\b(kleber|klebstoff|designbelagsklebstoff|dispersionskleber|kontaktkleber|parkettkleber)\b/i;
    const excludeRx = /\b(klebevinyl|klebevinylboden|klebe\-vinyl)\b/i;
    if (glueRx.test(`${t} ${ty} ${tg}`) && !excludeRx.test(`${t} ${ty} ${tg}`)) return 'kleber';

    if (isSockelProduct({ title, type: productType, tags }) && !isSockelAccessory(title)) return 'sockel';
    return 'piece';
  };

  // Decide if a cart item is MAIN for the given parent
  const isMainForParent = (it, parent) => {
    const props = it?.properties || {};
    if (String(props._fc || '') !== '1') return false;
    if (getFcGroup(props) === 'addon') return false;

    const p = String(props._fc_parent || '').trim();
    if (!p || p !== String(parent || '').trim()) return false;

    const role = getFcRole(props);
    const kind = getFcKind(props) || inferKindFromCart({
      title: it?.product_title || it?.title || '',
      productType: it?.product_type || '',
      tags: '',
      props
    });

    // strict main if role explicitly set
    if (role === 'main') return true;

    // fallback: treat as main only if it REALLY looks like floor (not tritt/sockel/kleber)
    return kind === 'floor';
  };

  const bestGlueSplit = (targetKg, has6, has14) => {
    const kg6 = 6;
    const kg14 = 14;
    if (targetKg <= 0) return { q6: 0, q14: 0 };

    const max14 = has14 ? Math.ceil(targetKg / kg14) + 2 : 0;
    const max6 = has6 ? Math.ceil(targetKg / kg6) + 3 : 0;

    let best = null;

    for (let q14 = 0; q14 <= max14; q14++) {
      for (let q6 = 0; q6 <= max6; q6++) {
        if (!has14 && q14 > 0) continue;
        if (!has6 && q6 > 0) continue;

        const totalKg = q14 * kg14 + q6 * kg6;
        if (totalKg < targetKg) continue;

        const over = totalKg - targetKg;
        const packs = q14 + q6;

        const cand = { q6, q14, over, packs };

        if (!best) { best = cand; continue; }
        if (cand.over < best.over) best = cand;
        else if (cand.over === best.over && cand.packs < best.packs) best = cand;
      }
    }

    if (!best) return { q6: 0, q14: 0 };
    return { q6: best.q6, q14: best.q14 };
  };

  const computeEffectivePkgM2 = ({ packageSize, props, title }) => {
    const packlikeM2Prop = getProp(props, ['_fc_packlike_m2', 'packlike_m2']);
    const packlikeM2Fallback = (() => {
      const tt = String(title || '').toLowerCase();
      if (tt.includes('pe standard') && tt.includes('2.0')) return 25;
      return 0;
    })();

    if (packageSize > 0) return packageSize;
    const v = toNum(packlikeM2Prop, 0) || packlikeM2Fallback;
    return v > 0 ? v : 0;
  };

  const getM2TotalFromMain = ({ qty, packageSize, props }) => {
    const fcUnit = String(getProp(props, ['_fc_unit']) || '').toLowerCase();
    const fcTotal = getProp(props, ['_fc_total']);
    if (fcTotal !== null && fcUnit === 'm2') return ceilInt(toNum(fcTotal, 0));

    const m2Prop = getProp(props, ['m2', 'm²']);
    if (m2Prop !== null) return ceilInt(toNum(m2Prop, 0));

    return ceilInt(round2((Number(qty) || 0) * (Number(packageSize) || 0)));
  };

  const buildDomBundleIndex = () => {
    const idx = new Map(); // parent -> { main: node, deps: [node...] }

    document.querySelectorAll(SELECTORS.meta).forEach(metaEl => {
      const itemRoot = metaEl.closest(SELECTORS.item);
      if (!itemRoot) return;

      const props = safeJson(metaEl.dataset.properties || '{}');
      if (!isFromCalculator(props)) return;
      if (isModalAddon(props)) return;

      const parent = getBundleParent(props);
      if (!parent) return;

      const qty = toNum(metaEl.dataset.qty, 0);
      const packageSize = toNum(metaEl.dataset.packageSize, 0);
      const lfmPerPiece = toNum(metaEl.dataset.lfmPerPiece, 0);

      const title = metaEl.dataset.title || '';
      const type = metaEl.dataset.productType || '';
      const tags = metaEl.dataset.tags || '';

      const m2Prop = getProp(props, ['m2', 'm²']);
      const lfmProp = getProp(props, ['lfm']);
      const fcUnit = getProp(props, ['_fc_unit']);

      const kind = detectKind({
        title, type, tags,
        packageSize,
        lfmPerPiece,
        hasM2Prop: m2Prop !== null,
        hasLfmProp: lfmProp !== null,
        fcUnit,
        overrideKind: metaEl.dataset.cartKindForced
      });

      const node = { metaEl, itemRoot, props, parent, kind, title, qty, packageSize, lfmPerPiece };

      if (!idx.has(parent)) idx.set(parent, { main: null, deps: [] });
      if (kind === 'floor') idx.get(parent).main = node;
      else idx.get(parent).deps.push(node);
    });

    return idx;
  };

  const scheduleParentSync = (parent) => {
    if (!BUNDLE.enabled) return;
    if (!isDrawerOpen()) return;
    if (!parent) return;

    const p = String(parent);
    const prev = BUNDLE.scheduled.get(p);
    if (prev) clearTimeout(prev);

    const t = setTimeout(() => {
      BUNDLE.scheduled.delete(p);
      syncBundleForParent(p).catch(err => console.error('[ld-cart-meta] bundle sync failed:', err));
    }, BUNDLE.debounceMs);

    BUNDLE.scheduled.set(p, t);
  };

  const shallowPropsEqual = (a, b) => {
    const A = a && typeof a === 'object' ? a : {};
    const B = b && typeof b === 'object' ? b : {};
    const keys = new Set([...Object.keys(A), ...Object.keys(B)]);
    for (const k of keys) {
      if (String(A[k] ?? '') !== String(B[k] ?? '')) return false;
    }
    return true;
  };

  // ---------- Orphan cleanup: remove deps when main is gone ----------
  const hasMainInCartForParent = (items, parent) => {
    const p = String(parent || '').trim();
    return (items || []).some(it => isMainForParent(it, p));
  };

  const scheduleOrphanCleanup = (parent) => {
    if (!BUNDLE.enabled) return;
    if (!isDrawerOpen()) return;
    if (!parent) return;

    const p = String(parent).trim();
    if (!p) return;

    const prev = BUNDLE.cleanupScheduled.get(p);
    if (prev) clearTimeout(prev);

    const t = setTimeout(() => {
      BUNDLE.cleanupScheduled.delete(p);
      cleanupOrphanDeps(p).catch(err => console.error('[ld-cart-meta] orphan cleanup failed:', err));
    }, BUNDLE.cleanupDebounceMs);

    BUNDLE.cleanupScheduled.set(p, t);
  };

  const cleanupOrphanDeps = async (parentId) => {
    if (!BUNDLE.enabled) return;
    if (!isDrawerOpen()) return;

    const parent = String(parentId || '').trim();
    if (!parent) return;

    const cart = await fetchCart();

    // Есть ли main?
    const hasMain = hasMainInCartForParent(cart.items, parent);
    dlog('cleanupOrphanDeps check', { parent, hasMain, items: (cart.items || []).length });
    if (hasMain) return;

    // Собираем deps (НЕ main), НЕ addon
    const orphanDeps = (cart.items || []).filter(it => {
      const props = it.properties || {};

      if (String(props._fc || '') !== '1') return false;

      const p = String(props._fc_parent || '').trim();
      if (p !== parent) return false;

      const role = String(props._fc_role || '').toLowerCase().trim();
      const kind = String(props._fc_kind || '').toLowerCase().trim();
      const group = String(props._fc_group || '').toLowerCase().trim();

      const removeWithMain = String(props._fc_remove_with_main || '') === '1';
      const title = String(it.product_title || it.title || '').toLowerCase();

      // main не трогаем
      const looksMain = (role === 'main') || (kind === 'floor');
      if (looksMain) return false;

      // удаляем:
      // 1) все deps (как раньше)
      // 2) И любые addon/offer, если явно помечены _fc_remove_with_main=1
      // 3) (страховка) любые "angebot" в названии, если вдруг метки нет
      if (removeWithMain) return true;
      if (group !== 'addon') return true;
      if (title.includes('angebot')) return true;

      return false;
    });

    dlog('cleanupOrphanDeps candidates', orphanDeps.map(it => ({
      key: it.key,
      title: it.product_title || it.title,
      role: getFcRole(it.properties || {}),
      group: getFcGroup(it.properties || {}),
      kind: getFcKind(it.properties || '') || '(infer)',
      parent: String((it.properties || {})._fc_parent || '')
    })));

    if (!orphanDeps.length) return;

    // ✅ one call
    const updates = {};
    orphanDeps.forEach(it => {
      const key = String(it.key || '').trim();
      if (key) updates[key] = 0;
    });

    if (!Object.keys(updates).length) return;

    dlog('cleanupOrphanDeps BULK UPDATE', updates);

    BUNDLE.isSyncing = true;
    try {
      await cartBulkUpdate(updates);
      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (_) {}
    } finally {
      BUNDLE.isSyncing = false;
    }
  };

  const syncBundleForParent = async (parentId) => {
    if (!BUNDLE.enabled) return;
    if (!parentId) return;
    if (!isDrawerOpen()) return;

    const parent = String(parentId);

    if (BUNDLE.syncingParents.has(parent)) return;
    BUNDLE.syncingParents.add(parent);
    BUNDLE.isSyncing = true;

    try {
      const idx = buildDomBundleIndex();
      const bundle = idx.get(parent);
      if (!bundle || !bundle.main) return;

      const main = bundle.main;
      const deps = bundle.deps || [];

      const m2Total = getM2TotalFromMain({
        qty: main.qty,
        packageSize: main.packageSize,
        props: main.props
      });

      const cart = await fetchCart();

      const byKey = new Map();
      (cart.items || []).forEach(it => {
        const key = String(it.key || '');
        if (!key) return;

        const props = it.properties || {};
        if (String(props._fc || '') !== '1') return;
        if (getFcGroup(props) === 'addon') return;

        const p = String(props._fc_parent || '').trim();
        if (!p || p !== parent) return;

        byKey.set(key, {
          key,
          quantity: Number(it.quantity || 0) || 0,
          properties: props,
          variantId: Number(it.variant_id || 0) || 0,
          title: String(it.product_title || it.title || '')
        });
      });

      let glue6 = null;
      let glue14 = null;

      deps.forEach(d => {
        const tt = String(d.title || '').toLowerCase();
        if (tt.includes('6') && (tt.includes('kg') || tt.includes(' 6'))) glue6 = d;
        if (tt.includes('14') && (tt.includes('kg') || tt.includes(' 14'))) glue14 = d;
        if (!glue6 && tt.includes('6 kg')) glue6 = d;
        if (!glue14 && tt.includes('14 kg')) glue14 = d;
      });

      const targetKg = m2Total * BUNDLE.glueKgPerM2;
      const glueSplit = bestGlueSplit(targetKg, !!glue6, !!glue14);

      const changes = [];

      for (const d of deps) {
        const lineKey = String(d.itemRoot?.dataset?.key || '').trim();
        if (!lineKey) continue;

        const cur = byKey.get(lineKey);
        if (!cur || !cur.key) continue;

        const nextProps = { ...(cur.properties || {}) };
        nextProps._fc_parent = parent;

        let nextQty = ceilInt(cur.quantity);

        if (d.kind === 'sockel') {
          const lfmTotal = ceilInt(m2Total);
          const per = Number(d.lfmPerPiece || 0);
          nextQty = (per > 0) ? Math.ceil(lfmTotal / per) : ceilInt(cur.quantity);

          nextProps._fc_unit = 'lfm';
          nextProps._fc_total = String(lfmTotal);
          nextProps.lfm = String(lfmTotal);
        }
        else if (d.kind === 'tritt') {
          const effPkg = computeEffectivePkgM2({ packageSize: d.packageSize, props: cur.properties, title: d.title });
          nextQty = (effPkg > 0) ? Math.ceil(m2Total / effPkg) : ceilInt(cur.quantity);

          nextProps._fc_unit = 'm2';
          nextProps._fc_total = String(ceilInt(m2Total));
          nextProps.m2 = String(ceilInt(m2Total));
        }
        else {
          const tt = String(d.title || '').toLowerCase();
          const glueRx = /\b(kleber|klebstoff|designbelagsklebstoff|dispersionskleber|kontaktkleber|parkettkleber)\b/i;
          const excludeRx = /\b(klebevinyl|klebevinylboden|klebe\-vinyl)\b/i;
          const isGlue = glueRx.test(tt) && !excludeRx.test(tt);
          if (!isGlue) continue;

          if (glue6 && d === glue6) nextQty = ceilInt(glueSplit.q6);
          if (glue14 && d === glue14) nextQty = ceilInt(glueSplit.q14);
        }

        const qtyChanged = Number(cur.quantity) !== Number(nextQty);
        const propsChanged = !shallowPropsEqual(cur.properties, nextProps);

        if (qtyChanged || propsChanged) {
          changes.push({ key: cur.key, quantity: nextQty, properties: nextProps });
        }
      }

      for (const ch of changes) {
        await cartChange(ch.key, ch.quantity, ch.properties);
      }

      try { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true })); } catch (_) {}
    } finally {
      BUNDLE.syncingParents.delete(parent);
      BUNDLE.isSyncing = false;
    }
  };

  // -----------------------
  // Main renderer for each line item meta
  // -----------------------
  const render = (metaEl) => {
    const itemRoot = metaEl.closest(SELECTORS.item);
    if (!itemRoot) return;

    const props = safeJson(metaEl.dataset.properties || '{}');

    const qty = toNum(metaEl.dataset.qty, 0);
    const finalLine = toNum(metaEl.dataset.finalLine, 0);
    const originalLine = toNum(metaEl.dataset.originalLine, 0);

    const variantPrice = toNum(metaEl.dataset.variantPrice, 0);
    const variantCompare = toNum(metaEl.dataset.variantCompare, 0);

    const packageSize = toNum(metaEl.dataset.packageSize, 0);
    const lfmPerPiece = toNum(metaEl.dataset.lfmPerPiece, 0);

    const title = metaEl.dataset.title || '';
    const type = metaEl.dataset.productType || '';
    const tags = metaEl.dataset.tags || '';

    const m2Prop = getProp(props, ['m2', 'm²']);
    const lfmProp = getProp(props, ['lfm']);

    const fcUnit = getProp(props, ['_fc_unit']);
    const fcTotal = getProp(props, ['_fc_total']);

    const hasM2Prop = m2Prop !== null;
    const hasLfmProp = lfmProp !== null;

    const kind = detectKind({ title, type, tags, packageSize, lfmPerPiece, hasM2Prop, hasLfmProp, fcUnit, overrideKind: metaEl.dataset.cartKindForced });

    // ✅ Kleber detector (for "UVP" label removal in meta box)
    const isKleberLine = (() => {
      const s = `${String(title || '').toLowerCase()} ${String(type || '').toLowerCase()} ${String(tags || '').toLowerCase()}`;
      const glueRx = /\b(kleber|klebstoff|designbelagsklebstoff|dispersionskleber|kontaktkleber|parkettkleber)\b/i;
      const excludeRx = /\b(klebevinyl|klebevinylboden|klebe\-vinyl)\b/i;
      return glueRx.test(s) && !excludeRx.test(s);
    })();

    // Muster handling
    const sample = isSampleLine(props) || String(itemRoot.dataset.ldSample || '') === 'true';
    if (sample) {
      setQtyHidden(itemRoot, true);
      metaEl.innerHTML = '';
      metaEl.style.display = 'none';
      syncRightPriceColumn(itemRoot, originalLine, finalLine, originalLine > finalLine);
      return;
    } else {
      setQtyHidden(itemRoot, false);
      metaEl.style.display = '';
    }

    // Unit label near qty
    const unitSpan = itemRoot.querySelector(SELECTORS.unit);
    if (unitSpan) {
      unitSpan.textContent = (kind === 'floor' || kind === 'tritt') ? 'Pakete' : 'Stück';
    }

    // Disable qty for calculator-managed add-ons incl. Kleber
    const disable = shouldDisableQty({ kind, title, type, tags, props });
    setQtyDisabled(itemRoot, disable);

    // For forced-kind lines (cart_kind_forced set in cart-item.liquid), use
    // exact math instead of ceilInt. Sub-unit packages (e.g. 0.5 m² Indo
    // Wandpaneele) must show the true value (0,5 m² for 1 pack), not a
    // ceiling to 1. Calculator flows still hit ceilInt via fcTotal path
    // above; non-forced simple products keep historic ceilInt behavior.
    const isForcedKind = ['floor','sockel','tritt','piece'].includes(
      String(metaEl.dataset.cartKindForced || '').toLowerCase()
    );

    // Locale display helper: 0.5 → "0,5", 10 → "10". Used only for entspricht.
    const fmtNum = (n) => String(n).replace('.', ',');

    const resolveM2Int = () => {
      if (fcTotal !== null && String(fcUnit || '').toLowerCase() === 'm2') return round2(toNum(fcTotal, 0));
      if (m2Prop !== null) return round2(toNum(m2Prop, 0));
      const raw = qty * packageSize;
      return isForcedKind ? round2(raw) : ceilInt(round2(raw));
    };

    const resolveLfmInt = () => {
      if (fcTotal !== null && String(fcUnit || '').toLowerCase() === 'lfm') return round2(toNum(fcTotal, 0));
      if (lfmProp !== null) return round2(toNum(lfmProp, 0));
      const raw = qty * lfmPerPiece;
      return isForcedKind ? round2(raw) : ceilInt(round2(raw));
    };

    const total = finalLine;

    const uvpFromCompare = (variantCompare > variantPrice && qty > 0) ? Math.round(variantCompare * qty) : 0;
    const totalUvp = Math.max(originalLine, uvpFromCompare);

    const hasDiscount = totalUvp > total;

    const perPackCents = (qty > 0) ? Math.round(total / qty) : 0;
    const comparePerPackCents = (qty > 0) ? Math.round(totalUvp / qty) : 0;

    const packlikeM2Prop = getProp(props, ['_fc_packlike_m2', 'packlike_m2']);
    const packlikeM2Fallback = (() => {
      const tt = String(title || '').toLowerCase();
      if (tt.includes('pe standard') && tt.includes('2.0')) return 25;
      return 0;
    })();

    const effectivePkgM2 = (() => {
      if (packageSize > 0) return packageSize;
      const v = toNum(packlikeM2Prop, 0) || packlikeM2Fallback;
      return v > 0 ? v : 0;
    })();

    const unitPricePerM2Cents = (effectivePkgM2 > 0) ? Math.round(perPackCents / effectivePkgM2) : 0;
    const unitComparePerM2Cents = (effectivePkgM2 > 0 && comparePerPackCents > perPackCents)
      ? Math.round(comparePerPackCents / effectivePkgM2)
      : 0;

    const unitPricePerLfmCents = (lfmPerPiece > 0) ? Math.round(perPackCents / lfmPerPiece) : 0;
    const unitComparePerLfmCents = (lfmPerPiece > 0 && comparePerPackCents > perPackCents)
      ? Math.round(comparePerPackCents / lfmPerPiece)
      : 0;

    const entsprichtM2 = resolveM2Int();
    const entsprichtLfm = resolveLfmInt();

    const saveCents = Math.max(0, totalUvp - total);

    let html = `<div class="ld-meta-box">`;

    if (kind === 'floor') {
      html += lineTpl(`Entspricht`, `<strong>${fmtNum(entsprichtM2)}</strong> m²`, ``);

      html += lineTpl(
        `Preis / m²`,
        money(unitPricePerM2Cents),
        (unitComparePerM2Cents > 0 ? money(unitComparePerM2Cents) : ``),
        'label'
      );

      html += lineTpl(
        `Preis / Paket`,
        `<strong>${money(perPackCents)}</strong>`,
        (comparePerPackCents > perPackCents ? money(comparePerPackCents) : ``),
        'label'
      );

      if (saveCents > 0) html += lineTpl(`Ersparnis`, `<strong>${money(saveCents)}</strong>`, ``);
    }
    else if (kind === 'sockel') {
      html += lineTpl(`Entspricht`, `<strong>${fmtNum(entsprichtLfm)}</strong> lfm`, ``);

      html += lineTpl(
        `Preis / lfm`,
        money(unitPricePerLfmCents),
        (unitComparePerLfmCents > 0 ? money(unitComparePerLfmCents) : ``),
        'strikeOnly'
      );

      if (saveCents > 0) html += lineTpl(`Ersparnis`, `<strong>${money(saveCents)}</strong>`, ``);
    }
    else if (kind === 'tritt') {
      html += lineTpl(`Entspricht`, `<strong>${fmtNum(entsprichtM2)}</strong> m²`, ``);

      html += lineTpl(
        `Preis / m²`,
        money(unitPricePerM2Cents),
        (unitComparePerM2Cents > 0 ? money(unitComparePerM2Cents) : ``),
        'strikeOnly'
      );

      html += lineTpl(
        `Preis / Paket`,
        `<strong>${money(perPackCents)}</strong>`,
        (comparePerPackCents > perPackCents ? money(comparePerPackCents) : ``),
        'strikeOnly'
      );

      if (saveCents > 0) html += lineTpl(`Ersparnis`, `<strong>${money(saveCents)}</strong>`, ``);
    }
    else {
      const perPiece = (qty > 0) ? Math.round(total / qty) : 0;
      const uvpPerPiece = (qty > 0 && comparePerPackCents > perPackCents) ? Math.round(totalUvp / qty) : 0;

      html += lineTpl(
        `Preis / Stück`,
        money(perPiece),
        (uvpPerPiece > 0 ? money(uvpPerPiece) : ``),
        (isKleberLine ? 'strikeOnly' : 'label')
      );

      if (saveCents > 0) html += lineTpl(`Ersparnis`, `<strong>${money(saveCents)}</strong>`, ``);
    }

    html += `</div>`;
    metaEl.innerHTML = html;

    syncRightPriceColumn(itemRoot, totalUvp, total, hasDiscount);

    // ✅ Trigger bundle sync on ACTUAL main qty change (post-render)
    if (BUNDLE.enabled && isDrawerOpen()) {
      if (isFromCalculator(props) && !isModalAddon(props)) {
        const parent = getBundleParent(props);
        if (parent && kind === 'floor') {
          const prev = BUNDLE.lastMainQtyByParent.get(parent);
          const cur = ceilInt(qty);

          if (prev === undefined) {
            BUNDLE.lastMainQtyByParent.set(parent, cur);
          } else if (prev !== cur) {
            BUNDLE.lastMainQtyByParent.set(parent, cur);
            scheduleParentSync(parent);
          }
        }
      }
    }

    // ✅ Orphan cleanup: for deps lines schedule cleanup
    if (BUNDLE.enabled && isDrawerOpen()) {
      if (isFromCalculator(props) && !isModalAddon(props)) {
        const parent = getBundleParent(props);
        if (parent && kind !== 'floor') {
          scheduleOrphanCleanup(parent);
        }
      }
    }
  };

  // -----------------------
  // Subtotal (stable source: cart.ajax.liquid hook)
  // -----------------------
  const getSubtotalCentsFromAjaxHook = () => {
    const el = document.querySelector(SELECTORS.ajaxSubtotalHook);
    if (!el) return null;
    const v = Number(el.getAttribute('data-cart-subtotal'));
    return Number.isFinite(v) ? v : null;
  };

  const updateSubtotals = () => {
    const cents = getSubtotalCentsFromAjaxHook();
    if (!Number.isFinite(cents)) return;

    document.querySelectorAll(SELECTORS.subtotals).forEach(el => {
      const next = money(cents);
      if (el.textContent.trim() !== next) el.textContent = next;
      if (el.getAttribute('data-subtotal-cents') !== String(cents)) {
        el.setAttribute('data-subtotal-cents', String(cents));
      }
    });
  };

  // -----------------------
  // Drawer "Sie sparen"
  // -----------------------
  const updateDrawerSavings = () => {
    const valueEl = document.querySelector(SELECTORS.savingsValue);
    if (!valueEl) return;

    let totalSave = 0;

    document.querySelectorAll(SELECTORS.meta).forEach(meta => {
      const qty = toNum(meta.dataset.qty, 0);

      const finalLine = toNum(meta.dataset.finalLine, 0);
      const originalLine = toNum(meta.dataset.originalLine, 0);

      const variantPrice = toNum(meta.dataset.variantPrice, 0);
      const variantCompare = toNum(meta.dataset.variantCompare, 0);

      const uvpFromCompare = (variantCompare > variantPrice && qty > 0)
        ? Math.round(variantCompare * qty)
        : 0;

      const totalUvp = Math.max(originalLine, uvpFromCompare);

      if (totalUvp > finalLine) totalSave += (totalUvp - finalLine);
    });

    const next = money(totalSave);
    if (valueEl.textContent !== next) valueEl.textContent = next;
  };

  // -----------------------
  // Runner
  // -----------------------
  const run = () => {
    if (BUNDLE.isSyncing) return;

    document.querySelectorAll(SELECTORS.meta).forEach(render);
    updateDrawerSavings();
    updateSubtotals();

    // ✅ if some parents were removed by clicking Entfernen, cleanup after rerender
    if (BUNDLE.pendingRemovedParents.size && isDrawerOpen()) {
      const now = Date.now();
      for (const [parent, ts] of BUNDLE.pendingRemovedParents.entries()) {
        if (now - ts > 5000) { BUNDLE.pendingRemovedParents.delete(parent); continue; }
        scheduleOrphanCleanup(parent);
        BUNDLE.pendingRemovedParents.delete(parent);
      }
    }
  };

  // -----------------------
  // Bindings (events + scoped observer + remove hook)
  // -----------------------
  const bind = () => {
    [
      'ajaxProduct:added',
      'cart:refresh',
      'cart:updated',
      'theme:cart:change',
      'theme:cart:open'
    ].forEach(ev => document.addEventListener(ev, () => requestAnimationFrame(run)));

    // ✅ Remove-click hook: detect MAIN floor remove, then cleanup deps after cart refresh
    document.addEventListener('click', (e) => {
      if (!isDrawerOpen()) return;

      const a = e.target && e.target.closest ? e.target.closest('a,button') : null;
      if (!a) return;

      const href = (a.getAttribute && a.getAttribute('href')) ? String(a.getAttribute('href')) : '';

      const looksRemove =
        href.includes('/cart/change') ||
        href.includes('quantity=0') ||
        a.classList.contains('cart__remove') ||
        a.matches('[data-cart-remove],[data-remove]') ||
        a.matches('button[name="remove"],button[data-action*="remove"],a[aria-label*="Entfern"],button[aria-label*="Entfern"]');

      if (!looksRemove) return;

      const itemRoot = a.closest(SELECTORS.item);
      if (!itemRoot) return;

      const metaEl = itemRoot.querySelector(SELECTORS.meta);
      if (!metaEl) return;

      const props = safeJson(metaEl.dataset.properties || '{}');
      if (!isFromCalculator(props) || isModalAddon(props)) return;

      const parent = getBundleParent(props);
      if (!parent) return;

      // determine kind from current DOM line
      const packageSize = toNum(metaEl.dataset.packageSize, 0);
      const lfmPerPiece = toNum(metaEl.dataset.lfmPerPiece, 0);
      const title = metaEl.dataset.title || '';
      const type = metaEl.dataset.productType || '';
      const tags = metaEl.dataset.tags || '';
      const m2Prop = getProp(props, ['m2', 'm²']);
      const lfmProp = getProp(props, ['lfm']);
      const fcUnit = getProp(props, ['_fc_unit']);

      const kind = detectKind({
        title, type, tags,
        packageSize,
        lfmPerPiece,
        hasM2Prop: m2Prop !== null,
        hasLfmProp: lfmProp !== null,
        fcUnit,
        overrideKind: metaEl.dataset.cartKindForced
      });

      dlog('remove click', { title, parent, kind, group: getFcGroup(props), role: getFcRole(props), unit: getFcUnit(props) });

      if (kind === 'floor') {
        BUNDLE.pendingRemovedParents.set(String(parent), Date.now());
      }
    }, true);

    const targets = [
      document.querySelector('#CartDrawer'),
      document.querySelector('.cart__page'),
      document.querySelector('[data-products]')
    ].filter(Boolean);

    const mo = new MutationObserver((mutations) => {
      if (BUNDLE.isSyncing) return;

      // Ignore our own meta box innerHTML updates
      if (mutations.some(m => m.target && m.target.closest && m.target.closest('.ld-meta-box'))) return;

      clearTimeout(bind._t);
      bind._t = setTimeout(() => requestAnimationFrame(run), 90);
    });

    targets.forEach(t => mo.observe(t, { childList: true, subtree: true }));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { run(); bind(); });
  } else {
    run(); bind();
  }
})();